/**
 * Planar geometry and raster primitives for the saya (shoe upper) yield engine.
 *
 * ── What this module is ─────────────────────────────────────────────────────
 *
 * This is an ESTIMATION engine, not a CAM nesting engine. Every polygon
 * operation that feeds an assessment (`scaleArea.ts`, `strip.ts`) is carried
 * out on a **square mm grid** whose cell size is configurable and defaults to
 * `DEFAULT_CELL_MM` = 1 mm. Rings are rasterised by scanline fill sampled at
 * cell centres, clearances are applied by dilating the raster with a digital
 * disk, and collisions are decided cell against cell. Nothing here does exact
 * polygon booleans or exact Minkowski sums; when the module ships alongside
 * cutter-facing geometry the exact work belongs in the pinned clipper2 build
 * (see the module design), not here.
 *
 * ── Documented tolerance ────────────────────────────────────────────────────
 *
 * At the default 1 mm cell the raster area of a CONVEX ring is within **2 %**
 * of its exact shoelace area for the part sizes this factory cuts (shoe upper
 * pieces, roughly 30–300 mm across). The bound behind that number is the
 * boundary band: centre sampling can only misjudge cells the outline passes
 * through, so
 *
 *     |A_raster − A_exact|  ≤  perimeter × cellMm / 2
 *
 * which for a 100 mm circle is 314 × 1 / 2 = 157 mm² against 7 854 mm², i.e.
 * 2.0 % worst case and far less in practice (the errors cancel, they do not
 * accumulate). Concave and thin-waisted parts can exceed that band — a feature
 * narrower than one cell can vanish entirely — so callers that care must lower
 * `cellMm`. Halving the cell halves the bound and quadruples the work.
 *
 * Areas reported to users are always computed from the EXACT ring
 * (`ringArea` / `partNetArea`); the raster is used only to decide how tightly
 * pieces can be packed. That keeps the reported net area exact and confines the
 * grid error to the packing term.
 *
 * ── Conventions ─────────────────────────────────────────────────────────────
 *
 * - Units are millimetres throughout. Rings are closed implicitly: the last
 *   point is joined back to the first, do not repeat it.
 * - Winding is irrelevant to every predicate here (fills and hit tests use the
 *   crossing rule, areas are taken in absolute value), so a ring may arrive
 *   CCW or CW.
 * - Holes are IGNORED by the net area — a punched hole does not reduce the
 *   leather a piece consumes (see `partNetArea`) — and stay OCCUPIED for
 *   placement: parts do not nest inside another part's cut-out unless the
 *   material config says they may. `partToRaster` takes that as an explicit
 *   flag, defaulting to "occupied", which is the factory's answer for v1.
 * - The file has NO imports on purpose. Like `api/auth.contract.ts` it is meant
 *   to compile unchanged in the Worker project, the SPA project and Node
 *   tooling, so the same assessment can run in a browser worker and be
 *   re-verified server-side.
 */

/* ── Types ───────────────────────────────────────────────────────────────── */

/** A point in millimetres, `[x, y]`, Y up. */
export type Point = [number, number]

/** A closed ring of points; the closing segment is implicit. */
export type Ring = Point[]

/**
 * One cuttable piece: an outer ring plus zero or more inner rings (cut-outs).
 * Inputs are accepted read-only so a caller can hand over frozen geometry
 * straight from a persisted revision.
 */
export interface Part {
  readonly outer: readonly Point[]
  readonly holes: readonly (readonly Point[])[]
}

/** Axis-aligned bounding box in millimetres. */
export interface BBox {
  minX: number
  minY: number
  maxX: number
  maxY: number
}

/**
 * An occupancy grid.
 *
 * Cell `(i, j)` of the raster is world cell `(originCellX + i, originCellY + j)`,
 * and world cell `(cx, cy)` covers the millimetre square
 * `[cx·cellMm, (cx+1)·cellMm) × [cy·cellMm, (cy+1)·cellMm)`. Keeping the origin
 * as an INTEGER cell index (rather than a millimetre offset) is what makes two
 * rasters comparable by plain integer arithmetic, and what makes a placement
 * offset an exact multiple of `cellMm` so the server can re-rasterise a
 * translated ring and land on identical cells.
 *
 * `data` is row-major, `data[j * width + i]`, 1 = occupied.
 */
export interface Raster {
  readonly cellMm: number
  readonly originCellX: number
  readonly originCellY: number
  readonly width: number
  readonly height: number
  readonly data: Uint8Array
}

/** Default assessment grid: 1 mm cells. See the tolerance note above. */
export const DEFAULT_CELL_MM = 1

/* ── Ring measures ───────────────────────────────────────────────────────── */

/**
 * Signed shoelace area in mm². Positive for counter-clockwise rings, negative
 * for clockwise ones — the sign is the cheapest orientation test there is.
 */
export function ringArea(ring: readonly Point[]): number {
  const n = ring.length
  if (n < 3) return 0
  let twice = 0
  let prev = ring[n - 1] as Point
  for (let i = 0; i < n; i++) {
    const cur = ring[i] as Point
    twice += prev[0] * cur[1] - cur[0] * prev[1]
    prev = cur
  }
  return twice / 2
}

/** Unsigned area in mm², orientation-independent. */
export function ringAbsArea(ring: readonly Point[]): number {
  return Math.abs(ringArea(ring))
}

/** Closed perimeter length in mm (includes the implicit closing segment). */
export function ringPerimeter(ring: readonly Point[]): number {
  const n = ring.length
  if (n < 2) return 0
  let total = 0
  let prev = ring[n - 1] as Point
  for (let i = 0; i < n; i++) {
    const cur = ring[i] as Point
    total += Math.hypot(cur[0] - prev[0], cur[1] - prev[1])
    prev = cur
  }
  return total
}

/**
 * Axis-aligned bounding box. Throws on an empty ring rather than returning
 * infinities — an empty ring is a data bug, not a degenerate shape.
 */
export function bbox(ring: readonly Point[]): BBox {
  if (ring.length === 0) throw new Error('bbox: ring is empty')
  let minX = Infinity
  let minY = Infinity
  let maxX = -Infinity
  let maxY = -Infinity
  for (let i = 0; i < ring.length; i++) {
    const p = ring[i] as Point
    if (p[0] < minX) minX = p[0]
    if (p[0] > maxX) maxX = p[0]
    if (p[1] < minY) minY = p[1]
    if (p[1] > maxY) maxY = p[1]
  }
  return { minX, minY, maxX, maxY }
}

/**
 * Area-weighted polygon centroid (the centre of mass of the filled ring, not
 * the average of the vertices). Falls back to the vertex average for a
 * degenerate ring of zero area, so the result is always finite.
 */
export function centroid(ring: readonly Point[]): Point {
  const n = ring.length
  if (n === 0) throw new Error('centroid: ring is empty')
  let twice = 0
  let cx = 0
  let cy = 0
  let prev = ring[n - 1] as Point
  for (let i = 0; i < n; i++) {
    const cur = ring[i] as Point
    const cross = prev[0] * cur[1] - cur[0] * prev[1]
    twice += cross
    cx += (prev[0] + cur[0]) * cross
    cy += (prev[1] + cur[1]) * cross
    prev = cur
  }
  if (twice === 0) {
    let sx = 0
    let sy = 0
    for (let i = 0; i < n; i++) {
      const p = ring[i] as Point
      sx += p[0]
      sy += p[1]
    }
    return [sx / n, sy / n]
  }
  return [cx / (3 * twice), cy / (3 * twice)]
}

/**
 * Net area of a part: the outer-ring area, in mm².
 *
 * Holes are deliberately ignored — the same rule as `netAreaMm2` in
 * `lib/saya/geometry.ts`: a punched hole does not reduce the leather a piece
 * consumes, and consumption is what yield is computed against. Legacy hole
 * rings still present on stored parts stay OCCUPIED in `partToRaster`, which is
 * the conservative direction for nesting.
 */
export function partNetArea(part: Part): number {
  return ringAbsArea(part.outer)
}

/* ── Ring transforms ─────────────────────────────────────────────────────── */

/**
 * Rotate a ring counter-clockwise about the ORIGIN by `deg` degrees.
 *
 * Quarter turns are special-cased to exact integer arithmetic. `Math.cos(π/2)`
 * is 6.1e-17, not 0, and that residue is enough to move a vertex that sits
 * exactly on a cell boundary into the neighbouring cell — which would make a
 * 90° strip rotation or the 180° çift-yön mate non-reproducible between the
 * browser worker and the Worker re-check. Everything else goes through the
 * ordinary rotation matrix.
 */
export function rotate(ring: readonly Point[], deg: number): Ring {
  // `0 - x` rather than `-x`: negating a zero coordinate yields -0, which
  // serialises the same but compares as a different value and would make a
  // "two runs are identical" check depend on where the origin sat.
  const turn = ((deg % 360) + 360) % 360
  if (turn === 0) return ring.map((p) => [p[0], p[1]] as Point)
  if (turn === 90) return ring.map((p) => [0 - p[1], p[0]] as Point)
  if (turn === 180) return ring.map((p) => [0 - p[0], 0 - p[1]] as Point)
  if (turn === 270) return ring.map((p) => [p[1], 0 - p[0]] as Point)
  const rad = (turn * Math.PI) / 180
  const cos = Math.cos(rad)
  const sin = Math.sin(rad)
  return ring.map((p) => [p[0] * cos - p[1] * sin, p[0] * sin + p[1] * cos] as Point)
}

/**
 * Reflect a ring HORIZONTALLY, `x → −x` (mirror across the Y axis). That is
 * the "aynalı çift" transform: the left and right instances of an upper part
 * differ by a horizontal flip, never a vertical one.
 *
 * The point order is reversed so the winding sign survives the reflection —
 * nothing in this module depends on winding, but downstream DXF export does.
 */
export function mirrorX(ring: readonly Point[]): Ring {
  const out: Ring = new Array(ring.length)
  for (let i = 0; i < ring.length; i++) {
    const p = ring[ring.length - 1 - i] as Point
    out[i] = [0 - p[0], p[1]]
  }
  return out
}

/** Translate a ring by `(dxMm, dyMm)`. */
export function translate(ring: readonly Point[], dxMm: number, dyMm: number): Ring {
  return ring.map((p) => [p[0] + dxMm, p[1] + dyMm] as Point)
}

/**
 * Crossing-rule (even-odd) hit test. A point exactly on the boundary is
 * reported consistently rather than correctly — the half-open `>` comparison
 * makes the answer stable, which is what a deterministic engine needs.
 */
export function pointInPolygon(point: Point, ring: readonly Point[]): boolean {
  const n = ring.length
  if (n < 3) return false
  const [x, y] = point
  let inside = false
  let prev = ring[n - 1] as Point
  for (let i = 0; i < n; i++) {
    const cur = ring[i] as Point
    if (prev[1] > y !== cur[1] > y) {
      const t = (y - prev[1]) / (cur[1] - prev[1])
      if (x < prev[0] + t * (cur[0] - prev[0])) inside = !inside
    }
    prev = cur
  }
  return inside
}

/* ── Rasterisation ───────────────────────────────────────────────────────── */

interface Frame {
  cellMm: number
  originCellX: number
  originCellY: number
  width: number
  height: number
}

function frameForRings(rings: readonly (readonly Point[])[], cellMm: number): Frame {
  let minX = Infinity
  let minY = Infinity
  let maxX = -Infinity
  let maxY = -Infinity
  for (let r = 0; r < rings.length; r++) {
    const ring = rings[r] as readonly Point[]
    for (let i = 0; i < ring.length; i++) {
      const p = ring[i] as Point
      if (p[0] < minX) minX = p[0]
      if (p[0] > maxX) maxX = p[0]
      if (p[1] < minY) minY = p[1]
      if (p[1] > maxY) maxY = p[1]
    }
  }
  if (!Number.isFinite(minX)) throw new Error('rasterise: no points')
  const originCellX = Math.floor(minX / cellMm)
  const originCellY = Math.floor(minY / cellMm)
  return {
    cellMm,
    originCellX,
    originCellY,
    width: Math.max(0, Math.ceil(maxX / cellMm) - originCellX),
    height: Math.max(0, Math.ceil(maxY / cellMm) - originCellY),
  }
}

/**
 * Scanline fill of one ring into an existing frame.
 *
 * For every grid row the sample line runs through the CELL CENTRES; edge
 * crossings are collected with the half-open `>` rule (identical to
 * `pointInPolygon`, so a cell centre and the hit test never disagree), sorted,
 * and the spans between consecutive pairs are filled. A cell is set when its
 * centre lies in `[xa, xb)`.
 */
function fillRing(frame: Frame, data: Uint8Array, ring: readonly Point[], value: 0 | 1): void {
  const n = ring.length
  if (n < 3) return
  const { cellMm, originCellX, originCellY, width, height } = frame
  const crossings: number[] = []
  for (let j = 0; j < height; j++) {
    const y = (originCellY + j + 0.5) * cellMm
    crossings.length = 0
    let prev = ring[n - 1] as Point
    for (let i = 0; i < n; i++) {
      const cur = ring[i] as Point
      if (prev[1] > y !== cur[1] > y) {
        const t = (y - prev[1]) / (cur[1] - prev[1])
        crossings.push(prev[0] + t * (cur[0] - prev[0]))
      }
      prev = cur
    }
    if (crossings.length < 2) continue
    crossings.sort((a, b) => a - b)
    const rowBase = j * width
    for (let s = 0; s + 1 < crossings.length; s += 2) {
      const xa = crossings[s] as number
      const xb = crossings[s + 1] as number
      let ia = Math.ceil(xa / cellMm - 0.5 - originCellX)
      let ib = Math.ceil(xb / cellMm - 0.5 - originCellX)
      if (ia < 0) ia = 0
      if (ib > width) ib = width
      for (let i = ia; i < ib; i++) data[rowBase + i] = value
    }
  }
}

/**
 * Rasterise a single ring at `cellMm` resolution. The returned raster is
 * bbox-tight: it starts at the first cell the ring can touch and ends at the
 * last, so two rasters of the same ring translated by a whole number of cells
 * differ only in their origin.
 */
export function ringToRaster(ring: readonly Point[], cellMm: number = DEFAULT_CELL_MM): Raster {
  if (!(cellMm > 0)) throw new Error('ringToRaster: cellMm must be > 0')
  const frame = frameForRings([ring], cellMm)
  const data = new Uint8Array(frame.width * frame.height)
  fillRing(frame, data, ring, 1)
  return { ...frame, data }
}

/**
 * Rasterise a whole part.
 *
 * `holesNestable` is the material-level cutter decision from the design: FALSE
 * (the default, and the factory's answer for v1) means a cut-out stays
 * OCCUPIED, so nothing nests inside it and the raster is simply the outer ring.
 * TRUE clears the holes and lets the packer drop pieces into them.
 *
 * Either way the reported net area comes from `partNetArea`, never from the
 * raster — and that area ignores holes entirely: a cut-out changes what can be
 * nested where, never what the part itself CONSUMES.
 */
export function partToRaster(
  part: Part,
  cellMm: number = DEFAULT_CELL_MM,
  options: { holesNestable?: boolean } = {},
): Raster {
  if (!(cellMm > 0)) throw new Error('partToRaster: cellMm must be > 0')
  const frame = frameForRings([part.outer], cellMm)
  const data = new Uint8Array(frame.width * frame.height)
  fillRing(frame, data, part.outer, 1)
  if (options.holesNestable === true) {
    for (let i = 0; i < part.holes.length; i++) {
      fillRing(frame, data, part.holes[i] as readonly Point[], 0)
    }
  }
  return { ...frame, data }
}

/**
 * Dilate an occupancy grid by a digital DISK of radius `cells` cells — the
 * gap/clearance operator.
 *
 * The result grows by `cells` on every side (origin moves, width and height
 * grow by `2·cells`) so nothing is clipped. Testing a dilated shape against an
 * undilated one is the clearance test used everywhere in this library:
 *
 *     dist(A, B) ≤ r   ⟺   (A ⊕ disk_r) ∩ B ≠ ∅   ⟺   A ∩ (B ⊕ disk_r) ≠ ∅
 *
 * which is symmetric, so only one of the two shapes ever needs dilating and
 * the order of the pair does not matter.
 *
 * A disk (not a square) is used deliberately: a Chebyshev square would demand
 * up to √2·gap of clearance on the diagonals and quietly cost yield. The digital
 * disk still rounds UP — a gap of `g` mm becomes `ceil(g / cellMm)` cells — so
 * the assessment is conservative, never optimistic, about clearance.
 */
export function rasterDilate(raster: Raster, cells: number): Raster {
  const r = Math.max(0, Math.trunc(cells))
  if (r === 0) return { ...raster, data: raster.data.slice() }
  const width = raster.width + 2 * r
  const height = raster.height + 2 * r
  const data = new Uint8Array(width * height)
  const offsets: number[] = []
  for (let dj = -r; dj <= r; dj++) {
    for (let di = -r; di <= r; di++) {
      if (di * di + dj * dj <= r * r) offsets.push(di, dj)
    }
  }
  const src = raster.data
  for (let j = 0; j < raster.height; j++) {
    const srcBase = j * raster.width
    for (let i = 0; i < raster.width; i++) {
      if (src[srcBase + i] === 0) continue
      const ci = i + r
      const cj = j + r
      for (let o = 0; o < offsets.length; o += 2) {
        const ni = ci + (offsets[o] as number)
        const nj = cj + (offsets[o + 1] as number)
        data[nj * width + ni] = 1
      }
    }
  }
  return {
    cellMm: raster.cellMm,
    originCellX: raster.originCellX - r,
    originCellY: raster.originCellY - r,
    width,
    height,
    data,
  }
}

/**
 * Grow a raster's frame by `cells` on every side WITHOUT dilating it, so it
 * shares an index frame with `rasterDilate(raster, cells)`. Every collision
 * search in this library compares a dilated raster with a padded one; keeping
 * both in one frame turns a clearance test into plain integer index arithmetic.
 */
export function rasterPad(raster: Raster, cells: number): Raster {
  const r = Math.max(0, Math.trunc(cells))
  if (r === 0) return { ...raster, data: raster.data.slice() }
  const width = raster.width + 2 * r
  const height = raster.height + 2 * r
  const data = new Uint8Array(width * height)
  for (let j = 0; j < raster.height; j++) {
    data.set(raster.data.subarray(j * raster.width, (j + 1) * raster.width), (j + r) * width + r)
  }
  return {
    cellMm: raster.cellMm,
    originCellX: raster.originCellX - r,
    originCellY: raster.originCellY - r,
    width,
    height,
    data,
  }
}

/** Number of occupied cells. */
export function rasterCount(raster: Raster): number {
  let count = 0
  const data = raster.data
  for (let i = 0; i < data.length; i++) if (data[i] !== 0) count++
  return count
}

/** Occupied area in mm² — the raster approximation of the ring's area. */
export function rasterArea(raster: Raster): number {
  return rasterCount(raster) * raster.cellMm * raster.cellMm
}

/**
 * Row-major run-length view of a raster: `runs[j]` is a flat list of half-open
 * column ranges `[start, end, start, end, …]` for row `j`.
 *
 * Every collision search here works on runs rather than cells. Two runs collide
 * for a contiguous INTERVAL of horizontal offsets, which turns "for which shifts
 * do these two shapes touch" from a per-cell correlation into a handful of
 * interval unions — the difference between an assessment that takes seconds and
 * one that takes milliseconds.
 */
export function rasterRuns(raster: Raster): number[][] {
  const runs: number[][] = new Array(raster.height)
  for (let j = 0; j < raster.height; j++) {
    const row: number[] = []
    const base = j * raster.width
    let i = 0
    while (i < raster.width) {
      if (raster.data[base + i] === 0) {
        i++
        continue
      }
      const start = i
      while (i < raster.width && raster.data[base + i] !== 0) i++
      row.push(start, i)
    }
    runs[j] = row
  }
  return runs
}
